import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type { Pillar } from '../types'
import { PILLARS as SEED_PILLARS } from '../studioConstants.js'
import { apiPillars } from '../studioApi.js'

// PillarsContext (Wave 23b) — replaces the static PILLARS constant with a
// runtime-fetched registry. Server can deprecate or propose pillars via
// the `pillars` table; without this context the frontend would render
// deprecated pillars in dropdowns and miss new proposed ones.
//
// Defensive design:
//   - Boot synchronously with seeded constants so the UI is never empty
//     during the initial fetch.
//   - On API failure, keep the seeded fallback. The five canonical pillars
//     are stable enough that a transient API outage shouldn't break the
//     create/edit flows.
//   - `activePillars` is the filtered list selectors should use. The full
//     `pillars` list (including deprecated/proposed) is exposed for
//     display lookups so historical posts still render their pillar
//     color/label correctly.

const SEED_PILLAR_REGISTRY: Pillar[] = SEED_PILLARS.map((p) => ({
  id: p.id,
  label: p.label,
  tag: p.tag,
  color: p.color,
  days: p.days,
  rgb: p.rgb || null,
  status: 'active',
}))

export interface PillarsContextValue {
  pillars: Pillar[]
  activePillars: Pillar[]
  loading: boolean
  error: string | null
  getPillar: (id: string | null | undefined) => Pillar
  reload: () => Promise<void>
}

const noop = async (): Promise<void> => {}

const PillarsContext = createContext<PillarsContextValue>({
  pillars: SEED_PILLAR_REGISTRY,
  activePillars: SEED_PILLAR_REGISTRY,
  loading: false,
  error: null,
  getPillar: (id) =>
    SEED_PILLAR_REGISTRY.find((p) => p.id === id) || SEED_PILLAR_REGISTRY[0],
  reload: noop,
})

export interface PillarsProviderProps {
  children?: ReactNode
  // initialPillars exists for tests — production boot reads from the API.
  initialPillars?: Pillar[]
  // skipAutoFetch lets tests opt out of the boot fetch.
  skipAutoFetch?: boolean
}

export function PillarsProvider({
  children,
  initialPillars,
  skipAutoFetch,
}: PillarsProviderProps) {
  const [pillars, setPillars] = useState<Pillar[]>(
    initialPillars && initialPillars.length
      ? initialPillars
      : SEED_PILLAR_REGISTRY
  )
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = (await apiPillars()) as { pillars?: Pillar[] }
      const next = Array.isArray(response?.pillars) ? response.pillars : []
      // Empty payload means the server hasn't run the migration yet —
      // keep whatever we had (seed or previous reload).
      if (next.length) {
        setPillars(next)
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'pillar registry fetch failed'
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (skipAutoFetch) return
    void reload()
  }, [reload, skipAutoFetch])

  const activePillars = useMemo<Pillar[]>(
    () => pillars.filter((p) => p.status === 'active'),
    [pillars]
  )

  const getPillar = useCallback(
    (id: string | null | undefined): Pillar => {
      const found =
        (!id ? pillars[0] || SEED_PILLAR_REGISTRY[0] : null) ||
        pillars.find((p) => p.id === id) ||
        // Fallback to seeded registry for historical IDs that aren't in
        // the live registry (e.g., a wholly removed pillar an old post
        // still references).
        SEED_PILLAR_REGISTRY.find((p) => p.id === id) ||
        pillars[0] ||
        SEED_PILLAR_REGISTRY[0]
      // Calm-palette override (2026-06-10): the DB seeds four SATURATED pillar
      // hues (gold/blue/purple/green) that render as dots/tags/borders across
      // ~10 surfaces — the dominant "colors fighting" source. A product is
      // calm: pillars carry identity through their LABEL + tag number, not a
      // clashing rainbow. We neutralise the colour at this single chokepoint
      // (a warm grey that sits quietly with the cream/charcoal palette) so the
      // whole studio calms at once. Restore per-pillar hues here if the
      // operator wants colour coding back — but muted, value-matched, please.
      return { ...found, color: '#8c857a', rgb: '140,133,122' }
    },
    [pillars]
  )

  const value = useMemo<PillarsContextValue>(
    () => ({
      pillars,
      activePillars,
      loading,
      error,
      getPillar,
      reload,
    }),
    [pillars, activePillars, loading, error, getPillar, reload]
  )

  return (
    <PillarsContext.Provider value={value}>{children}</PillarsContext.Provider>
  )
}

export function usePillars(): PillarsContextValue {
  return useContext(PillarsContext)
}

export { PillarsContext, SEED_PILLAR_REGISTRY }
